import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';

import {
  NotebookFilter,
  Option,
  useCountries,
  useMarkets,
  useNetworks,
  useNotebooks
} from 'apiTyped/hooks/notebooks';
import theme from 'theme';

import Card from 'components/Card';
import Select from 'components/Select/SelectFilter';
import Pills from 'components/Pills';
import { useMemo } from 'react';

const Container = styled.div`
  .row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 25px;
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    display: none;
    .row {
      grid-template-columns: 1fr;
      align-items: center;
    }
  }
`;

const START_YEAR = 2023;

type NotebookFilterProps = {
  filterValue: NotebookFilter;
  // eslint-disable-next-line no-unused-vars
  onFilterChange: (filter: NotebookFilter) => void;
};

const formatLabel = (label: string, count?: number) => {
  if (!count) {
    return label;
  }

  return `${label} (${count})`;
};

const subscriptionFilterStyles = `
  flex-grow: 1;
`;

export const NotebookFilters = ({
  filterValue,
  onFilterChange
}: NotebookFilterProps) => {
  const { countriesResponse } = useCountries();
  const { marketResponse } = useMarkets({
    countryId: filterValue.country?.value
  });
  const { networksResponse, isLoadingNetworks } = useNetworks();

  const networkOptions =
    networksResponse
      ?.filter((network) => ['OFF', 'ON'].includes(network.code))
      .map((network) => ({
        label: I18n.t(
          `notebooks.filters.network.items.${network.code}.${filterValue.productType}`
        ),
        value: network.id
      })) || [];

  const onCountryChange = (country: Option) => {
    onFilterChange({ ...filterValue, country, market: null });
  };

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - START_YEAR + 1 },
    (_, i) => (START_YEAR + i).toString()
  );

  const { notebooksResponse: filteredNotebooks } = useNotebooks({
    ...filterValue,
    subscribed: false
  });

  const subscribedNotebooksCount = filteredNotebooks?.filter((notebook) => {
    return notebook.subscribed;
  });

  const sortedMarkets = useMemo(() => {
    return marketResponse?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [marketResponse]);

  const sortedCountries = useMemo(() => {
    return countriesResponse?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [countriesResponse]);

  return (
    <Container
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}
    >
      <Card>
        <div className="row">
          {isLoadingNetworks ? null : (
            <Pills
              options={networkOptions.map(({ label, value }) => ({
                label,
                value,
                active: filterValue.network?.value === value,
                styles: `
                  width: 68px;
                  flex-grow: 1;
                  flex-shrink: 1;
                `
              }))}
              onChange={(network: Option) => {
                onFilterChange({ ...filterValue, network });
              }}
              styles={`
                justify-content: space-between;
                min-height: 26px;
                width: fit-content;
                text-wrap: nowrap;

                @media (max-width: ${
                  theme.devices.isTablet ? '0px' : '845px'
                }) {
                  width: stretch;
                }
              `}
            />
          )}
          <Select
            dropdownIcon={null}
            isDisabled={false}
            selectedOption={filterValue.country}
            options={
              sortedCountries?.map((country) => ({
                label: country.name,
                value: country.id
              })) || []
            }
            onChange={onCountryChange}
            label={I18n.t('notebooks.filters.countries.title')}
            placeholder={I18n.t('placeholderAll')}
            minWidth="100px"
          />
          <Select
            dropdownIcon={null}
            isDisabled={false}
            selectedOption={filterValue.market}
            options={
              sortedMarkets?.map((market) => ({
                label: market.name,
                value: market.id
              })) || []
            }
            onChange={(market: Option) => {
              onFilterChange({ ...filterValue, market });
            }}
            label={I18n.t('notebooks.filters.markets.title')}
            placeholder={I18n.t('placeholderAll')}
            minWidth="100px"
          />
          <Select
            dropdownIcon={null}
            isDisabled={false}
            selectedOption={filterValue.year}
            options={yearOptions.map((year) => ({
              label: year,
              value: year
            }))}
            onChange={(year: Option) => {
              onFilterChange({ ...filterValue, year });
            }}
            label={I18n.t('notebooks.filters.year.title')}
            placeholder={I18n.t('placeholderAll')}
            minWidth="100px"
          />
        </div>
        <div className="row">
          <Pills
            options={[
              {
                label: formatLabel(
                  I18n.t('notebooks.filters.subscribed'),
                  subscribedNotebooksCount?.length
                ),
                value: true,
                active: filterValue.subscribed === true,
                styles: subscriptionFilterStyles
              },
              {
                label: formatLabel(
                  I18n.t('notebooks.filters.all'),
                  filteredNotebooks?.length
                ),
                value: false,
                active: filterValue.subscribed === false,
                styles: subscriptionFilterStyles
              }
            ]}
            onChange={(status: { value: boolean }) => {
              onFilterChange({ ...filterValue, subscribed: status.value });
            }}
            styles={`
              justify-content: space-between;
              min-height: 26px;
              width: fit-content;

              @media (max-width: ${theme.devices.isTablet ? '0px' : '845px'}) {
                  width: stretch;
                }
            `}
          />
        </div>
      </Card>
    </Container>
  );
};
